/**
 * Сотрудники платформы и их доступы.
 *
 * Отключение вместо удаления: в журнале остаются решения этого человека, и по
 * ним должно быть видно, кто их принял. Последнего живого суперадмина нельзя
 * ни разжаловать, ни отключить — иначе в админку не войдёт никто, и чинить
 * придётся руками в базе.
 */
import type { Admin } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Skeleton, fmtShortDay } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

const ROLE_NOTE: Record<string, string> = {
  superadmin: 'всё, включая сотрудников и доступы',
  moderator: 'модерация отзывов, брендов, акций и полосы',
  support: 'только чтение',
};

export function StaffScreen() {
  const { me, canManageStaff } = useAdmin();
  const data = useResource(
    () => (canManageStaff ? api.adminStaff() : Promise.resolve(null)),
    [canManageStaff],
  );
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<Admin> | null>(null);

  const toggle = async (admin: Admin) => {
    try {
      await api.adminSaveStaff({
        id: admin.id,
        email: admin.email,
        name: admin.name,
        role: admin.role,
        disabled: !admin.disabled_at,
      });
      toast(admin.disabled_at ? 'Доступ возвращён' : 'Доступ отключён');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <Layout
      title="Сотрудники"
      subtitle="кто и что может делать на платформе"
      action={
        canManageStaff ? (
          <Button variant="pri" icon="plus" onClick={() => setEditing({ role: 'moderator' })}>
            Добавить
          </Button>
        ) : undefined
      }
    >
      {!canManageStaff && (
        <Empty icon="shield" title="Доступ только у суперадмина">
          Список сотрудников — это карта доступов платформы: кто что может делать. Читать её
          модератору и поддержке незачем.
        </Empty>
      )}

      {canManageStaff && data.loading && <Skeleton count={3} height={48} />}

      {data.data && (
        <Card>
          <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>Почта</th>
                <th>Имя</th>
                <th>Роль</th>
                <th>Заведён</th>
                <th>Состояние</th>
                {canManageStaff && <th />}
              </tr>
            </thead>
            <tbody>
              {data.data.admins.map((admin) => (
                <tr key={admin.id}>
                  <td className="mono">{admin.email}</td>
                  <td>{admin.name || '—'}</td>
                  <td>
                    <div>{me?.roles[admin.role] ?? admin.role}</div>
                    <div className="faint" style={{ fontSize: 11.5 }}>
                      {ROLE_NOTE[admin.role]}
                    </div>
                  </td>
                  <td className="faint">{fmtShortDay(admin.created_at)}</td>
                  <td>
                    {admin.disabled_at ? (
                      <Badge tone="bad">отключён</Badge>
                    ) : (
                      <Badge tone="good">активен</Badge>
                    )}
                  </td>
                  {canManageStaff && (
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <Button size="sm" onClick={() => setEditing(admin)}>
                          Править
                        </Button>
                        <Button
                          size="sm"
                          variant={admin.disabled_at ? 'out' : 'danger'}
                          onClick={() => void toggle(admin)}
                        >
                          {admin.disabled_at ? 'Включить' : 'Отключить'}
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {editing && (
        <StaffDialog
          admin={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

function StaffDialog({
  admin,
  onClose,
  onDone,
}: {
  admin: Partial<Admin>;
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useAdmin();
  const toast = useToast();
  const [draft, setDraft] = useState({
    id: admin.id,
    email: admin.email ?? '',
    name: admin.name ?? '',
    role: admin.role ?? 'moderator',
    password: '',
    disabled: Boolean(admin.disabled_at),
  });
  const [busy, setBusy] = useState(false);

  const isNew = !draft.id;
  const submit = async () => {
    setBusy(true);
    try {
      await api.adminSaveStaff(draft);
      toast(isNew ? 'Сотрудник заведён' : 'Сохранено');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="h2">{isNew ? 'Новый сотрудник' : draft.email}</div>
        <p className="sub" style={{ margin: '6px 0 14px' }}>
          Пароль задаётся здесь и показывается один раз — передай его лично. Публичной
          регистрации в админке нет.
        </p>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Почта</span>
            <input
              className="input mono"
              value={draft.email}
              disabled={!isNew}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Имя</span>
            <input
              className="input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Роль</span>
            <select
              className="select"
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value as Admin['role'] })}
            >
              {Object.entries(me?.roles ?? {}).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="lab">{isNew ? 'Пароль' : 'Новый пароль — если нужно'}</span>
            <input
              className="input mono"
              type="password"
              autoComplete="new-password"
              placeholder={isNew ? 'не короче 10 символов' : 'оставь пустым, чтобы не менять'}
              value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            />
          </label>
        </div>

        <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
          {ROLE_NOTE[draft.role]}
        </div>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button
            variant="pri"
            disabled={busy || !draft.email.includes('@') || (isNew && draft.password.length < 10)}
            onClick={submit}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </div>
  );
}
